import { isDevPreview } from '../boot/devPreview';
import { sb } from './supabase';

/**
 * 실시간 접속자 표시 · 객체 잠금 (작업지시서 P4-13)
 *
 * ── 왜 "잠금" 이 아니라 "누가 잡고 있다" 인가 ───────────────────────────
 *
 *   진짜 배타 잠금(DB 행 잠금)은 브라우저에서 못 쓴다. 탭이 그냥 닫히면
 *   잠금이 영원히 안 풀리기 때문이다. 그래서 **소프트 잠금** 을 쓴다:
 *
 *     · Presence 채널에 "내가 지금 이 객체들을 잡고 있다" 를 방송한다
 *     · 남이 잡은 객체를 편집하려 하면 **경고하고 막는다**
 *     · 연결이 끊기면 Presence 가 자동으로 사라져 잠금도 풀린다
 *
 *   ⚠️ 이건 동시 편집 충돌을 **줄이는** 장치지 없애는 장치가 아니다.
 *      진짜 보장은 저장 시점의 op-log 순번(cad_operation.seq unique)이 한다.
 *      화면에서 막는 이유는 "두 사람이 같은 매대를 30분씩 따로 옮기는"
 *      낭비를 없애기 위해서다.
 */

export interface Peer {
  email: string;
  name: string;
  /** 이 사람이 잡고 있는 객체 id */
  holding: string[];
  /** 마지막 커서 위치 (µm). 없으면 화면 밖 */
  cursor: { x: number; y: number } | null;
  color: string;
  at: number;
}

/** 사람마다 고정 색. 같은 사람이 항상 같은 색이어야 눈에 익는다. */
const PALETTE = [
  '#5fd3f3',
  '#7ee08a',
  '#ffd166',
  '#ff9ecd',
  '#c9a0ff',
  '#ffab76',
  '#8ce0d0',
];

export function colorFor(email: string): string {
  let h = 0;
  for (let i = 0; i < email.length; i++) h = (h * 31 + email.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length]!;
}

export interface PresenceHandle {
  /** 지금 접속해 있는 사람들 (나 제외) */
  peers(): Peer[];
  /** 내가 잡고 있는 객체를 알린다 */
  setHolding(ids: string[]): void;
  /** 내 커서 위치를 알린다 (throttle 은 호출자 책임) */
  setCursor(p: { x: number; y: number } | null): void;
  /** 이 객체를 남이 잡고 있는가 */
  heldBy(entityId: string): Peer | null;
  close(): void;
}

export interface PresenceOptions {
  versionId: string;
  me: { email: string; name: string };
  onChange: () => void;
}

/**
 * Presence 채널에 접속한다.
 *
 * 미리보기 모드나 접속 실패 시에도 **동작하는 빈 핸들**을 돌려준다.
 * 실시간이 안 된다고 편집기가 못 열리면 안 된다.
 */
export function joinPresence(options: PresenceOptions): PresenceHandle {
  const state = new Map<string, Peer>();
  let holding: string[] = [];
  let cursor: { x: number; y: number } | null = null;

  const base: PresenceHandle = {
    peers: () => [...state.values()].filter((p) => p.email !== options.me.email),
    setHolding: (ids) => {
      holding = ids;
    },
    setCursor: (p) => {
      cursor = p;
    },
    heldBy: (entityId) => {
      for (const p of state.values()) {
        if (p.email === options.me.email) continue;
        if (p.holding.includes(entityId)) return p;
      }
      return null;
    },
    close: () => {
      state.clear();
    },
  };

  if (isDevPreview()) return base;

  let channel: ReturnType<ReturnType<typeof sb>['channel']> | null = null;

  try {
    channel = sb().channel(`cad:version:${options.versionId}`, {
      config: { presence: { key: options.me.email } },
    });

    const sync = () => {
      state.clear();
      const raw = channel!.presenceState() as Record<string, unknown[]>;
      for (const entries of Object.values(raw)) {
        const first = entries[0] as Partial<Peer> | undefined;
        if (!first?.email) continue;
        state.set(first.email, {
          email: first.email,
          name: first.name ?? first.email,
          holding: first.holding ?? [],
          cursor: first.cursor ?? null,
          color: colorFor(first.email),
          at: first.at ?? Date.now(),
        });
      }
      options.onChange();
    };

    channel
      .on('presence', { event: 'sync' }, sync)
      .on('presence', { event: 'join' }, sync)
      .on('presence', { event: 'leave' }, sync)
      .subscribe((status: string) => {
        if (status === 'SUBSCRIBED') void push();
      });
  } catch {
    // 실시간이 안 되는 환경에서도 편집기는 열려야 한다
    return base;
  }

  async function push(): Promise<void> {
    if (!channel) return;
    try {
      await channel.track({
        email: options.me.email,
        name: options.me.name,
        holding,
        cursor,
        at: Date.now(),
      });
    } catch {
      /* 방송 실패는 편집을 막지 않는다 */
    }
  }

  return {
    ...base,
    setHolding: (ids) => {
      // 같은 내용이면 방송하지 않는다 — 선택할 때마다 채널이 시끄러워진다
      if (ids.length === holding.length && ids.every((v, i) => v === holding[i])) return;
      holding = ids;
      void push();
    },
    setCursor: (p) => {
      cursor = p;
      void push();
    },
    close: () => {
      state.clear();
      try {
        void channel?.unsubscribe();
      } catch {
        /* 이미 닫혔으면 무시 */
      }
    },
  };
}

/**
 * 이 편집을 지금 해도 되는가.
 *
 * 남이 잡고 있는 객체가 섞여 있으면 **막고 누가 잡고 있는지 알려준다.**
 * 그냥 진행시키면 나중에 한쪽 작업이 통째로 덮인다.
 */
export function checkConflict(
  ids: string[],
  presence: PresenceHandle
): { ok: true } | { ok: false; message: string } {
  const blocked = new Map<string, Peer>();
  for (const id of ids) {
    const peer = presence.heldBy(id);
    if (peer) blocked.set(peer.email, peer);
  }

  if (blocked.size === 0) return { ok: true };

  const who = [...blocked.values()].map((p) => p.name).join(', ');
  return {
    ok: false,
    message: `${who} 님이 편집 중인 객체가 포함되어 있습니다. 잠시 후 다시 시도하세요.`,
  };
}
